import React, { Component, createRef, Dispatch } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import {
    AbstractSofaForm,
    maxLengthFilter,
    SofaForm,
    SofaFormControl,
    SofaFormControlDescription,
    SofaFormValidator,
    stringRequiredValidator,
    MAX_FIELD_LENGTH,
    regexpValidator,
    EMAIL_REGEXP,
    MAX_EMAIL_LENGTH,
    textFilter,
    getPropsForSofaInput,
    SofaInput,
    PASSWORD_REQUIRED_ERROR_MESSAGE
} from '@sofology/sofologycomponents';

import './LoginForm.scss';
import { errorToBasketErrorModel, generateDispatchToPropsMap, NOT_ALLOW_ANY_SPACES_REGEX } from 'helpers';
import {
    EMAIL_NOT_VALID_ERROR_MESSAGE,
    EMAIL_REQUIRED_ERROR_MESSAGE,
    INVALID_LOGIN_DETAILS_MESSAGE
} from '../DeliveryDetailsForm/deliveryDetailsAdditionalData';
import { SofaFormComponent } from 'interfaces';
import { LoginFormData } from '../CheckoutAuthentication/CheckoutAuthentication';
import { AppState } from 'store';
import { BasketErrorModel } from 'models/Errors';
import { checkEmail, CheckEmailFunc, clearLoginAuthErrors, ClearLoginAuthErrorsFunc } from 'store/checkout/actions';

interface OwnProps {
}

interface OwnPropsWithDefault {
    ref?: React.RefObject<LoginFormType>;
    formData: LoginFormData;
    goToRegistrationPage: () => void;
    hasAuthErrors: boolean;
}

interface OwnState {
    loginForm: SofaForm<LoginFormControls>;
}

interface StateProps {
    userExists: boolean | null;
    loginErrors: BasketErrorModel | null;
    basketId: string;
}

interface DispatchProps {
    checkEmail: CheckEmailFunc;
    clearLoginAuthErrors: ClearLoginAuthErrorsFunc;
}

interface LoginFormControls extends AbstractSofaForm {
    email: SofaFormControl<string>;
    password: SofaFormControl<string>;
}

class LoginForm extends Component<OwnProps & OwnPropsWithDefault & StateProps & DispatchProps, OwnState> implements SofaFormComponent<LoginFormData> {
    static defaultProps: Partial<OwnPropsWithDefault> = {
        formData: {
            email: '',
            password: ''
        }
    };

    constructor(props: OwnProps & OwnPropsWithDefault & StateProps & DispatchProps) {
        super(props);

        this.state = {
            loginForm: new SofaForm<LoginFormControls>(
                {
                    email: {
                        name: 'email',
                        defaultValue: this.props.formData.email,
                        filters: [
                            maxLengthFilter(MAX_EMAIL_LENGTH),
                            textFilter(NOT_ALLOW_ANY_SPACES_REGEX)
                        ],
                        validators: [
                            stringRequiredValidator(EMAIL_REQUIRED_ERROR_MESSAGE),
                            regexpValidator(EMAIL_NOT_VALID_ERROR_MESSAGE, EMAIL_REGEXP)
                        ],
                        ref: createRef<SofaInput>(),
                    } as SofaFormControlDescription<string>,
                    password: {
                        name: 'password',
                        defaultValue: this.props.formData.password,
                        filters: [
                            maxLengthFilter(MAX_FIELD_LENGTH)
                        ],
                        validators: [
                            stringRequiredValidator(PASSWORD_REQUIRED_ERROR_MESSAGE),
                            this.passwordServerValidator()
                        ],
                        ref: createRef<SofaInput>()
                    } as SofaFormControlDescription<string>,
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'loginForm',
                () => this.state,
                errorToBasketErrorModel
            )
        };
    }

    componentDidMount(): void {
        this.state.loginForm.controls.email.setValue(this.props.formData.email);
        this.state.loginForm.controls.password.setValue(this.props.formData.password);
    }

    componentDidUpdate(prevProps: Readonly<OwnProps & StateProps & DispatchProps>, prevState: Readonly<OwnState>): void {
        if (prevProps.userExists === null && this.props.userExists === false) {
            this.validate();
        }

        if (prevState.loginForm.controls.email.value !== this.state.loginForm.controls.email.value) {
            if (this.props.userExists !== null) {
                this.props.clearLoginAuthErrors();
            }

            if (this.state.loginForm.controls.password.isBlurHandled
                && this.state.loginForm.controls.email.isBlurHandled) {
                this.state.loginForm.controls.password.setValue(this.state.loginForm.controls.password.value);
            }
        }
    }

    handleGoToRegistrationPageClick = () => {
        this.props.goToRegistrationPage();
    };

    shouldMakeEmailInputBorderInvalid = (): boolean => {
        const { email, password } = this.state.loginForm.controls;

        return Boolean(
            (password.value !== '' && password.errorMessage) || email.errorMessage
        );
    };

    getInputProps = (control: SofaFormControl<any>, addErrorClassOverride?: boolean) => ({
        ...getPropsForSofaInput(control),
        addErrorClass: this.props.hasAuthErrors || (addErrorClassOverride ?? Boolean(control.errorMessage)),
        helperText: this.props.hasAuthErrors ? null : control.errorMessage
    });

    render(): JSX.Element {
        return (
            <div className='login-form'>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Email address*'
                            shape='pill'
                            inputProps={{ inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'email' }}
                            {...this.getInputProps(
                                this.state.loginForm.controls.email,
                                this.shouldMakeEmailInputBorderInvalid()
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} className={`multiline-error ${this.state.loginForm.controls.password.errorMessage ? 'MuiGrid-grid-xs-12--error' : ''}`}>
                        <SofaInput
                            label='Password*'
                            type='password'
                            shape='pill'
                            inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', autoComplete: 'current-password' }}
                            {...this.getInputProps(this.state.loginForm.controls.password)}
                        />
                    </Grid>
                </Grid>
            </div>
        );
    }

    renderAccountDoesntExistError(shouldShowError: boolean): JSX.Element | null {
        return shouldShowError ? <span>{INVALID_LOGIN_DETAILS_MESSAGE}</span> : null;
    }

    renderPasswordIssuesError(shouldShowError: boolean): JSX.Element | null {
        return shouldShowError ? <span>{INVALID_LOGIN_DETAILS_MESSAGE}</span> : null;
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        return this.state.loginForm.validate();
    };

    public getFormData(): LoginFormData {
        return {
            email: this.state.loginForm.controls.email.value,
            password: this.state.loginForm.controls.password.value
        };
    }

    passwordServerValidator = (): SofaFormValidator<JSX.Element> => {
        return (value: string, isChangedAfterExternalValidation: boolean) => {
            if (isChangedAfterExternalValidation) {
                return Promise.resolve(null);
            }

            if (this.props.userExists === false) {
                return Promise.resolve(this.renderAccountDoesntExistError(true));
            }

            const shouldShowPasswordError = Boolean(
                this.props.loginErrors
                && this.props.userExists === true
            );

            return Promise.resolve(this.renderPasswordIssuesError(shouldShowPasswordError));
        };
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        userExists: state.checkoutState.userExists,
        loginErrors: state.checkoutState.credentialErrors,
        basketId: state.basketState.basket!.guid
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [checkEmail, clearLoginAuthErrors]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(LoginForm);
export type LoginFormType = LoginForm;